/**
 * Lead Tracking Provider
 * Initializes UTM capture, visitor tracking, click ID capture, and behavioral tracking on app load
 */

import { useEffect } from 'react';
import { trackScrollDepth as trackScrollAnalytics, trackSectionView } from '@/lib/analytics';
import { initWebVitals } from '@/lib/webVitals';
import { captureClickIds } from '@/lib/clickIdTracking';
import { trackDeepScroll as pushDeepScrollEvent, trackReturnVisitor, trackTimeEngagement } from '@/lib/retargetingEvents';

// UTM parameter keys we track
const UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const;

// Storage keys
const VISITOR_ID_KEY = 'solar_visitor_id';
const UTM_DATA_KEY = 'solar_utm_data';
const VISIT_COUNT_KEY = 'solar_visit_count';
const FIRST_VISIT_KEY = 'solar_first_visit';

interface UTMData {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  referrer?: string;
  landing_page?: string;
}

/**
 * Generate a unique visitor ID
 */
const generateVisitorId = (): string => {
  return 'v_' + Math.random().toString(36).substring(2) + Date.now().toString(36);
};

/**
 * Initialize visitor tracking
 */
const initializeVisitor = () => {
  if (typeof window === 'undefined') return;
  
  // Get or create visitor ID
  let visitorId = localStorage.getItem(VISITOR_ID_KEY);
  if (!visitorId) {
    visitorId = generateVisitorId();
    localStorage.setItem(VISITOR_ID_KEY, visitorId);
    localStorage.setItem(FIRST_VISIT_KEY, new Date().toISOString());
    console.log('[Lead Tracking] New visitor:', visitorId);
  } else {
    console.log('[Lead Tracking] Returning visitor:', visitorId);
  }
  
  // Increment visit count (once per session)
  const sessionKey = 'solar_session_counted';
  if (!sessionStorage.getItem(sessionKey)) {
    const currentCount = parseInt(localStorage.getItem(VISIT_COUNT_KEY) || '0', 10);
    localStorage.setItem(VISIT_COUNT_KEY, (currentCount + 1).toString());
    sessionStorage.setItem(sessionKey, 'true');
    console.log('[Lead Tracking] Visit count:', currentCount + 1);
  }
};

/**
 * Capture and store UTM parameters
 */
const captureUTMParams = () => {
  if (typeof window === 'undefined') return;
  
  const urlParams = new URLSearchParams(window.location.search);
  const newUTMData: UTMData = {};
  
  // Capture UTM params from URL
  UTM_PARAMS.forEach((param) => {
    const value = urlParams.get(param);
    if (value) {
      newUTMData[param] = value;
    }
  });
  
  // Get existing stored data
  let existingData: UTMData = {};
  try {
    const stored = localStorage.getItem(UTM_DATA_KEY);
    existingData = stored ? JSON.parse(stored) : {};
  } catch {
    existingData = {};
  }
  
  // Capture referrer (only if not already set and external)
  if (!existingData.referrer && document.referrer && !document.referrer.includes(window.location.hostname)) {
    newUTMData.referrer = document.referrer;
  }
  
  // Capture landing page (only on first visit)
  if (!existingData.landing_page) {
    newUTMData.landing_page = window.location.pathname + window.location.search;
  }
  
  // Merge with first-touch attribution (existing data takes precedence)
  const mergedData: UTMData = {
    ...newUTMData,
    ...existingData,
  };
  
  // Always keep most recent landing page
  if (newUTMData.landing_page) {
    mergedData.landing_page = newUTMData.landing_page;
  }
  
  // Store merged data
  if (Object.keys(mergedData).length > 0) {
    localStorage.setItem(UTM_DATA_KEY, JSON.stringify(mergedData));
    
    if (Object.keys(newUTMData).length > 0) {
      console.log('[Lead Tracking] UTM data captured:', newUTMData);
    }
  }
};

// Auto-capture UTM params on module load (before React redirects)
captureUTMParams();

/**
 * Track scroll depth and section visibility
 */
const setupScrollTracking = () => {
  if (typeof window === 'undefined') return;
  
  const BEHAVIOR_DATA_KEY = 'solar_behavior_data';
  const trackedSections = new Set<string>();
  
  const handleScroll = () => {
    // Calculate scroll depth
    const scrollTop = window.scrollY;
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;
    const scrollPercent = Math.round((scrollTop / docHeight) * 100);
    
    // Track scroll depth
    trackScrollAnalytics(scrollPercent);
    
    // Push retargeting event for deep scrollers
    pushDeepScrollEvent(scrollPercent);
    
    // Update behavioral data for max scroll
    try {
      const stored = localStorage.getItem(BEHAVIOR_DATA_KEY);
      const behavior = stored ? JSON.parse(stored) : {};
      if (scrollPercent > (behavior.maxScrollDepth || 0)) {
        behavior.maxScrollDepth = scrollPercent;
        localStorage.setItem(BEHAVIOR_DATA_KEY, JSON.stringify(behavior));
      }
    } catch (e) {
      // Silently fail
    }
    
    // Track section visibility
    const sections = ['hero', 'calculator', 'benefits', 'how-it-works', 'testimonials', 'gallery', 'faq', 'schedule'];
    sections.forEach(sectionId => {
      const section = document.getElementById(sectionId);
      if (section && !trackedSections.has(sectionId)) {
        const rect = section.getBoundingClientRect();
        const isVisible = rect.top < window.innerHeight * 0.5 && rect.bottom > 0;
        
        if (isVisible) {
          trackedSections.add(sectionId);
          trackSectionView(sectionId);
          
          // Update behavioral data for section views
          try {
            const stored = localStorage.getItem(BEHAVIOR_DATA_KEY);
            const behavior = stored ? JSON.parse(stored) : {};
            
            if (sectionId === 'testimonials') behavior.testimonialsViewed = true;
            if (sectionId === 'benefits') behavior.benefitsViewed = true;
            if (sectionId === 'gallery') behavior.galleryViewed = true;
            
            localStorage.setItem(BEHAVIOR_DATA_KEY, JSON.stringify(behavior));
          } catch (e) {
            // Silently fail
          }
        }
      }
    });
  };
  
  // Throttle scroll handler
  let ticking = false;
  const throttledScroll = () => {
    if (!ticking) {
      window.requestAnimationFrame(() => {
        handleScroll();
        ticking = false;
      });
      ticking = true;
    }
  };
  
  window.addEventListener('scroll', throttledScroll, { passive: true });
  
  // Initial check
  handleScroll();
  
  return () => {
    window.removeEventListener('scroll', throttledScroll);
  };
};

/**
 * Lead Tracking Provider Component
 * Renders nothing but initializes tracking on mount
 */
export const LeadTrackingProvider = ({ children }: { children: React.ReactNode }) => {
  useEffect(() => {
    // Initialize on mount
    initializeVisitor();
    captureUTMParams();
    captureClickIds();
    
    // Initialize Web Vitals tracking
    initWebVitals();
    
    // Fire retargeting events for return visitors
    const visitCount = parseInt(localStorage.getItem(VISIT_COUNT_KEY) || '1', 10);
    if (visitCount > 1) {
      trackReturnVisitor(visitCount);
    }
    
    // Track time engagement after 3 minutes
    const timeEngagementTimer = setTimeout(() => {
      trackTimeEngagement(180);
    }, 180000);
    
    // Set up scroll and section tracking
    const cleanupScroll = setupScrollTracking();
    
    return () => {
      if (cleanupScroll) cleanupScroll();
      clearTimeout(timeEngagementTimer);
    };
  }, []);
  
  return <>{children}</>;
};

export default LeadTrackingProvider;
